import React from 'react';
import Text from 'design-system/Text';
import { tierThemes } from 'features/premium/benefits';
import { periodStringMap, TierPeriod } from 'features/premium/entities';
import { formatAmount, formatDateForItem, formatPlural, } from 'utils/formatting';
/** Emphasised (medium-weight) inline value — prices, dates, counts. */
const Value = ({ children, number, }) => (<Text {...(number ? { 'Numbers-14': true } : { 'Text-14': true })} Secondary>
    {children}
  </Text>);
const periodWord = (period) => period === TierPeriod.yearly ? 'year' : 'month';
const subjectName = (model) => model.context === 'extraMember'
    ? 'Extra member'
    : tierThemes[model.tier].name;
const promoLabel = (model) => {
    const { promo, period } = model;
    if (!promo)
        return null;
    if (promo.appliesToNextBill) {
        if (period === TierPeriod.yearly) {
            return (<>
          {promo.label} discount applies to your payment on{' '}
          <Value>{formatDateForItem(model.renewal.date)}</Value>.
        </>);
        }
        return (<>
        {promo.label} discount applies for your next{' '}
        <Value>
          {promo.durationMonths
                ? `${promo.durationMonths} ${formatPlural('month', 'months', promo.durationMonths)}`
                : 'month'}
        </Value>
        .
      </>);
    }
    const periodLabel = promo.durationMonths ? (<Value>
      {promo.durationMonths}{' '}
      {formatPlural('month', 'months', promo.durationMonths)}
    </Value>) : (periodWord(period));
    return (<>
      {promo.label} discount applies for your first {periodLabel}.
    </>);
};
const renewalSentence = (model) => (<>
    Your subscription will renew at{' '}
    <Value number>{formatAmount(model.renewal.price, model.currency, 2)}</Value>
    {periodStringMap[model.period]} on{' '}
    <Value>{formatDateForItem(model.renewal.date)}</Value>.
  </>);
const extraMemberOffer = (model) => {
    const { slots } = model;
    if (!slots?.offer)
        return null;
    const { count } = slots;
    const slotStr = formatPlural('slot', 'slots', count);
    const { durationMonths, originalPricePerPeriod, payToday } = slots.offer;
    const isOneMonthOffer = durationMonths === 1;
    return (<>
      By clicking below, you agree to a{' '}
      <Value number>{formatAmount(payToday, model.currency, 2)}</Value> charge
      today, which covers your first month’s subscription for {count} {slotStr}.{' '}
      {isOneMonthOffer ? (<>After this month, the subscription renews at the full price of </>) : (<>
          You’ll then be charged{' '}
          <Value number>
            {formatAmount(slots.pricePerPeriod, model.currency, 2)}
          </Value>
          /mo for the next {durationMonths - 1}{' '}
          {formatPlural('month', 'months', durationMonths - 1)}, after which the
          subscription renews at the full price of{' '}
        </>)}
      <Value number>
        {formatAmount(originalPricePerPeriod, model.currency, 2)}
      </Value>
      /mo.
    </>);
};
/**
 * Render functions for each copy fragment (the Figma A–I library plus the trial
 * prefix and the preserved extra-member discount-offer copy). Each returns
 * inline content; `CalculationDescription` joins them with spaces inside a
 * single grey-box `Text`.
 */
export const fragmentRenderers = {
    trialPrefix: (model) => (<>
      Free trial ends in{' '}
      <Value>
        {model.trialDaysRemaining}{' '}
        {formatPlural('day', 'days', model.trialDaysRemaining ?? 0)}
      </Value>
      .
    </>),
    trialStart: () => <>Your free trial starts today.</>,
    // Figma: when today's charge equals the recurring price, A drops the price and
    // reads "…will automatically renew on {date}." (restating the price is redundant).
    A: (model) => model.payToday !== model.renewal.price ? (<>
        Your {subjectName(model)} subscription starts today. It will
        automatically renew at{' '}
        <Value number>
          {formatAmount(model.renewal.price, model.currency, 2)}
        </Value>
        {periodStringMap[model.period]} on{' '}
        <Value>{formatDateForItem(model.renewal.date)}</Value>.
      </>) : (<>
        Your {subjectName(model)} subscription starts today. It will
        automatically renew on{' '}
        <Value>{formatDateForItem(model.renewal.date)}</Value>.
      </>),
    'A-1': (model) => <>Your {subjectName(model)} subscription starts today.</>,
    'B-1': (model) => (<>
      {model.annualDiscountPercentage
            ? `${model.annualDiscountPercentage}% annual`
            : 'Annual'}{' '}
      discount is locked in for every year you stay on the annual plan.
    </>),
    'B-2': (model) => (<>
      Thereafter, your{' '}
      {model.annualDiscountPercentage
            ? `${model.annualDiscountPercentage}% `
            : ''}
      annual discount is locked in for life.
    </>),
    C: (model) => renewalSentence(model),
    // Recurring-discount renewal: today's discounted rate reverts to the full
    // price on a later date. Resolver only selects this over C when they differ.
    'C-2': (model) => (<>
      {model.period === TierPeriod.monthly ? 'Thereafter, your' : 'Your'}{' '}
      subscription will renew at{' '}
      <Value number>
        {formatAmount(model.fullPriceRenewal.price, model.currency, 2)}
      </Value>
      {periodStringMap[model.period]} on{' '}
      <Value>{formatDateForItem(model.fullPriceRenewal.date)}</Value>.
    </>),
    D: (model) => promoLabel(model),
    E: (model) => (<>
      Remaining{' '}
      <Value number>
        {formatAmount(model.credit?.remaining ?? 0, model.currency, 2)}
      </Value>{' '}
      credit will be applied to future payments.
    </>),
    F: () => <>You won’t be charged until your credit runs out.</>,
    G: () => <>We’re refunding the remaining balance back to your account.</>,
    H: (model) => (<>
      After first year, subscription will renew at{' '}
      <Value number>
        {formatAmount(model.renewal.price, model.currency, 2)}
      </Value>
      {periodStringMap[model.period]} on{' '}
      <Value>{formatDateForItem(model.renewal.date)}</Value>.
    </>),
    I: (model) => {
        const { slots } = model;
        if (!slots)
            return null;
        const slotStr = formatPlural('slot', 'slots', slots.count);
        return (<>
        Starting <Value>{formatDateForItem(slots.renewalDate)}</Value>,{' '}
        <Value number>
          {formatAmount(slots.pricePerPeriod, model.currency, 2)}
        </Value>
        /mo for {slots.count} {slotStr} will be charged.
      </>);
    },
    extraMemberOffer: (model) => extraMemberOffer(model),
};
